import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ActivatedRoute, CanDeactivateFn, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ConfirmationDialogComponent } from '../../shared/ui/confirmation-dialog/confirmation-dialog.component';
import {
  DedupeColumn, DedupeService, DedupeTable, DuplicateGroup, DuplicateRecord, DuplicateReview, DuplicateScan,
} from './dedupe.service';

@Component({
  selector: 'app-dedupe',
  templateUrl: './dedupe.component.html',
  styleUrls: ['./dedupe.component.css'],
})
export class DedupeComponent implements OnInit, OnDestroy {
  table: DedupeTable = 'leads';
  available: DedupeColumn[] = [];
  chosen = new Set<string>();
  scan?: DuplicateScan;
  review?: DuplicateReview;
  selected?: DuplicateGroup;
  get survivorId(): number { return this.selected?.records[0]?.id ?? 0; }
  differencesOnly = false;
  mergeResult: Record<string, string | null> = {};
  fieldSources: Record<string, number | undefined> = {};
  private readonly drafts = new Map<string, { token: string; mergeResult: Record<string, string | null>; fieldSources: Record<string, number | undefined> }>();
  page = 1;
  readonly pageSize = 25;
  skipped = new Set<string>();
  loadingColumns = false;
  scanning = false;
  reviewing = false;
  reviewInvalid = false;
  mutating = false;
  confirming = false;
  mergedTotal = 0;
  needsRefresh = false;
  error = '';
  notice = '';
  private readonly destroy$ = new Subject<void>();
  private confirmationRef?: MatDialogRef<ConfirmationDialogComponent>;
  private scanRequest?: Subscription;
  private reviewRequest?: Subscription;

  constructor(
    private dedupe: DedupeService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.table = this.route.snapshot.data['dedupeTable'];
    this.auth.tenant.sessionChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.destroy$.next();
      this.scanRequest?.unsubscribe();
      this.reviewRequest?.unsubscribe();
      this.scan = undefined;
      this.review = undefined;
      this.mergeResult = {}; this.fieldSources = {}; this.drafts.clear();
      this.needsRefresh = false;
      this.mutating = false; this.confirming = false;
      this.router.navigate(['..'], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    });
    this.loadColumns();
  }

  ngOnDestroy(): void {
    this.destroy$.next(); this.destroy$.complete();
    this.confirmationRef?.close(false);
    this.scanRequest?.unsubscribe(); this.reviewRequest?.unsubscribe();
  }

  get tableLabel(): string { return this.table === 'rolloff-hires' ? 'roll-off deals' : this.table; }
  get groupRecord(): DuplicateRecord | undefined {
    const first = this.selected?.records[0];
    return this.review?.records.find(record => record.id === first?.id) ?? first;
  }
  get keptRecordId(): number | undefined {
    const fields = this.review?.editableFields ?? [];
    return fields.length ? this.review?.records.find(record => fields.every(name =>
      this.fieldSources[name] === record.id && this.mergeResult[name] === (record.values[name] ?? null)))?.id : undefined;
  }
  recordLabel(index: number): string {
    if (index < 0) return '';
    return (index < 26 ? '' : String.fromCharCode(64 + Math.floor(index / 26)))
      + String.fromCharCode(65 + index % 26);
  }
  recordTitle(record?: DuplicateRecord): string {
    const values = record?.values;
    return values?.['name'] || values?.['displayName'] || values?.['siteName'] || record?.label || 'Unnamed record';
  }
  recordAddress(record?: DuplicateRecord): string {
    const fields = this.table === 'customers'
      ? ['serviceAddressLine1', 'serviceCity', 'serviceState', 'serviceZip']
      : ['addressLine1', 'city', 'state', 'postalCode'];
    return fields.map(field => record?.values[field]).filter(Boolean).join(', ');
  }
  get canManage(): boolean {
    return this.auth.hasPermission({ leads: 'Lead.Manage', customers: 'Customer.Manage', 'rolloff-hires': 'LeadPipeline.Manage' }[this.table]);
  }
  get busy(): boolean { return this.mutating || this.confirming; }
  get hasMatchingFields(): boolean { return this.chosen.size > 0; }
  get chosenColumns(): string[] { return this.available.filter(column => this.chosen.has(column.name)).map(column => column.name); }
  get chosenFields(): DedupeColumn[] { return this.available.filter(column => this.chosen.has(column.name)); }
  get remainingFields(): DedupeColumn[] { return this.available.filter(column => !this.chosen.has(column.name)); }
  get matchingFieldsChanged(): boolean { return !!this.scan && this.chosenColumns.join('|') !== this.scan.referenceColumns.join('|'); }
  get pageCount(): number { return Math.max(1, Math.ceil((this.scan?.totalGroups ?? 0) / this.pageSize)); }
  get actionable(): boolean { return this.canManage && !this.busy && !this.scanning && !this.reviewing && !this.matchingFieldsChanged && !this.reviewInvalid && !!this.review; }
  get unresolvedFields(): string[] {
    return (this.review?.editableFields ?? []).filter(name => !this.mergeResult[name]?.trim()
      && (this.review?.afterMerge[name]?.trim() || this.review?.records.some(record => record.values[name]?.trim())));
  }
  get choicesNeededLabel(): string { return `${this.unresolvedFields.length} ${this.unresolvedFields.length === 1 ? 'choice' : 'choices'} needed`; }
  get canMerge(): boolean { return this.actionable && !this.unresolvedFields.length; }
  isEditable(name: string): boolean { return !!this.review?.editableFields?.includes(name); }
  isUnresolved(name: string): boolean { return this.unresolvedFields.includes(name); }
  resultValue(name: string): string | null {
    return (this.isEditable(name) ? this.mergeResult[name] : this.review?.afterMerge[name]) ?? null;
  }
  isMatchField(name: string): boolean { return this.scan?.referenceColumns.includes(name) ?? false; }
  fieldKey(_index: number, field: DedupeColumn): string { return field.name; }
  recordKey(_index: number, record: DuplicateRecord): number { return record.id; }
  groupKey(_index: number, group: DuplicateGroup): string { return group.key; }
  selectedValueRecord(name: string): number | undefined {
    const record = this.review?.records.find(item => item.id === this.fieldSources[name]);
    return record && (record.values[name] ?? null) === this.mergeResult[name] ? record.id : undefined;
  }
  useValue(name: string, recordId: number): void {
    const record = this.review?.records.find(item => item.id === recordId);
    if (!this.actionable || !this.isEditable(name) || !record) return;
    this.mergeResult[name] = record.values[name] ?? null;
    this.fieldSources[name] = record.id;
  }
  editResult(name: string, value: string): void {
    if (!this.actionable || !this.isEditable(name)) return;
    this.mergeResult[name] = value.trim() ? value : null;
    delete this.fieldSources[name];
  }
  private rememberDraft(): void {
    if (this.selected && this.review && !this.reviewInvalid) this.drafts.set(this.selected.key, {
      token: this.review.token,
      mergeResult: { ...this.mergeResult }, fieldSources: { ...this.fieldSources },
    });
  }
  queueStatus(group: DuplicateGroup): string {
    if (this.skipped.has(group.key)) return 'Skipped';
    if (group.key !== this.selected?.key || !this.review || this.matchingFieldsChanged || this.reviewInvalid || this.reviewing || this.scanning) return '';
    if (this.unresolvedFields.length) return this.choicesNeededLabel;
    return 'Ready to merge';
  }
  get visibleFields(): DedupeColumn[] {
    return this.available.filter(field => !this.differencesOnly || this.differs(field.name) || this.isUnresolved(field.name) || this.resultValue(field.name) !== (this.review?.afterMerge[field.name] ?? null));
  }

  close(): void {
    if (!this.busy) this.router.navigate(['..'], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  loadColumns(): void {
    this.loadingColumns = true;
    this.dedupe.columns(this.table).pipe(takeUntil(this.destroy$)).subscribe({
      next: columns => { this.loadingColumns = false; this.available = columns; },
      error: err => { this.loadingColumns = false; this.error = this.reason(err, 'Matching fields could not be loaded.'); },
    });
  }

  toggle(name: string): void {
    if (this.busy || this.loadingColumns) return;
    if (this.chosen.has(name)) this.chosen.delete(name); else this.chosen.add(name);
    this.scanRequest?.unsubscribe(); this.reviewRequest?.unsubscribe();
    this.scanning = false; this.reviewing = false; this.reviewInvalid = true;
    this.error = ''; this.notice = '';
  }

  replaceField(old: string, name: string): void {
    if (this.busy || this.loadingColumns) return;
    this.toggle(old); this.toggle(name);
  }

  foldNote(column: DedupeColumn): string {
    if (column.fold === 'Address') return 'Ignores case, punctuation and address abbreviations';
    if (column.fold === 'Phone') return 'Compares the last ten digits';
    return 'Ignores case and extra spacing';
  }

  find(): void {
    if (this.busy || this.scanning || this.loadingColumns || !this.hasMatchingFields) return;
    this.error = ''; this.notice = ''; this.skipped.clear(); this.drafts.clear();
    this.runScan();
  }

  runScan(page = 1, preferredKey?: string): void {
    if (!this.hasMatchingFields || this.busy) return;
    this.scanRequest?.unsubscribe(); this.reviewRequest?.unsubscribe();
    this.rememberDraft();
    this.page = page; this.scanning = true; this.reviewing = false;
    this.reviewInvalid = true;
    this.scanRequest = this.dedupe.duplicates(this.table, this.chosenColumns, page, this.pageSize).pipe(takeUntil(this.destroy$)).subscribe({
      next: scan => {
        this.scanning = false;
        this.scan = scan;
        if (page > this.pageCount) { this.runScan(this.pageCount); return; }
        const next = scan.groups.find(group => group.key === preferredKey)
          ?? scan.groups.find(group => !this.skipped.has(group.key));
        if (next) this.select(next);
        else { this.selected = undefined; this.review = undefined; this.mergeResult = {}; this.fieldSources = {}; }
      },
      error: err => { this.scanning = false; this.error = this.reason(err, 'Duplicates could not be loaded.'); },
    });
  }

  select(group: DuplicateGroup): void {
    if (this.busy || this.scanning || this.matchingFieldsChanged) return;
    this.reviewRequest?.unsubscribe(); this.reviewing = false;
    if (this.selected?.key === group.key && this.review && !this.reviewInvalid) return;
    this.rememberDraft();
    if (group.recordCount > 100) {
      this.selected = group; this.review = undefined; this.mergeResult = {}; this.fieldSources = {}; this.reviewInvalid = true;
      return;
    }
    this.loadReview(group);
  }

  keepRecord(id: number, checked = true): void {
    if (!this.actionable) return;
    if (!checked) { this.fieldSources = {}; return; }
    for (const name of this.review?.editableFields ?? []) this.useValue(name, id);
  }

  loadReview(group = this.selected): void {
    if (!group || group.recordCount > 100 || this.busy || this.scanning || this.matchingFieldsChanged) return;
    this.rememberDraft();
    const saved = this.selected?.key === group.key && this.review
      ? { token: this.review.token, mergeResult: { ...this.mergeResult }, fieldSources: { ...this.fieldSources } }
      : this.drafts.get(group.key);
    const primary = group.records[0].id;
    this.reviewRequest?.unsubscribe(); this.reviewing = true; this.reviewInvalid = true;
    if (!this.review) this.selected = group;
    this.reviewRequest = this.dedupe.review(this.table, primary,
      group.records.filter(record => record.id !== primary).map(record => record.id), this.scan!.referenceColumns)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: review => {
          this.reviewing = false; this.reviewInvalid = false;
          this.selected = group; this.review = review;
          this.mergeResult = Object.fromEntries((review.editableFields ?? []).map(name => [name,
            saved && name in saved.mergeResult ? saved.mergeResult[name]
              : review.conflicts?.includes(name) ? null : review.afterMerge[name] ?? null,
          ]));
          this.fieldSources = Object.fromEntries((review.editableFields ?? []).map(name => [name,
            saved ? saved.fieldSources[name] : this.mergeResult[name]?.trim()
              ? review.records.find(record => record.values[name] === this.mergeResult[name])?.id : undefined,
          ]));
          this.error = '';
          if (saved && saved.token !== review.token)
            this.notice = 'Records changed. Check the updated values before merging.';
        },
        error: err => { this.reviewing = false; this.error = this.reason(err, 'Records could not be loaded. Try again.'); },
      });
  }

  otherIds(): number[] { return this.selected?.records.filter(record => record.id !== this.survivorId).map(record => record.id) ?? []; }
  differs(name: string): boolean {
    return new Set(this.review?.records.map(record => record.values[name] ?? '')).size > 1;
  }

  skip(): void {
    if (this.busy || this.reviewing || this.scanning || this.matchingFieldsChanged || !this.selected) return;
    this.rememberDraft();
    this.skipped.add(this.selected.key);
    const next = this.scan?.groups.find(group => !this.skipped.has(group.key));
    if (next) this.select(next);
    else if (this.page < this.pageCount) this.runScan(this.page + 1);
    else { this.reviewRequest?.unsubscribe(); this.reviewing = false; this.selected = undefined; this.review = undefined; }
  }

  confirmMerge(): void {
    if (!this.canMerge || !this.selected || !this.review) return;
    const ids = this.otherIds();
    const primary = this.survivorId;
    const key = this.selected.key;
    const previous = this.review;
    const values = { ...this.mergeResult };
    this.confirming = true; this.error = '';
    this.confirmationRef = this.dialog.open(ConfirmationDialogComponent, { data: {
      title: `Merge ${ids.length + 1} records?`,
      message: `Keep #${primary} and remove ${ids.length} duplicate${ids.length === 1 ? '' : 's'}. ${previous.consequence} This cannot be undone here.`,
      confirmLabel: 'Save & merge', danger: true,
    } });
    this.confirmationRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
      this.confirming = false;
      if (!confirmed) return;
      this.mutating = true; this.needsRefresh = true; this.error = ''; this.notice = '';
      this.dedupe.merge(this.table, primary, ids, previous.token, this.scan!.referenceColumns, values).pipe(takeUntil(this.destroy$)).subscribe({
        next: result => {
          this.mutating = false; this.drafts.delete(key); this.reviewInvalid = true; this.mergedTotal += result.merged;
          this.notice = result.refused.length
            ? `Merged ${result.merged}. Not merged: ${result.refused.map(item => '#' + item.id + ' (' + item.reason + ')').join('; ')}`
            : `Merged ${result.merged} duplicate${result.merged === 1 ? '' : 's'}.`;
          this.runScan(this.page, result.refused.length ? key : undefined);
        },
        error: err => {
          this.mutating = false;
          this.error = this.reason(err, 'Merge could not be confirmed. Refresh the comparison before retrying.');
          if (!err.status || err.status >= 500 || err.status === 409) this.reviewInvalid = true;
        },
      });
    });
  }

  private reason(err: unknown, fallback: string): string {
    return (err as { error?: { error?: string } })?.error?.error || fallback;
  }
}

/** Keep the active write mounted until its outcome is known. */
export const canDeactivateDedupe: CanDeactivateFn<DedupeComponent> = component => !component.busy;
